const defaultTheme = require('tailwindcss/defaultTheme')
const { brandColor } = require('@metamask/design-tokens')
const plugin = require('tailwindcss/plugin')

const thirdPartyColors = {
  /* eslint-disable sort-keys-fix/sort-keys-fix */
  // Third party brand colors
  akropolis: '#2d3d66',
  aragon: '#2CC0E1',
  brave: '#ff2000',
  uniswap: '#FF007A',
  colony: '#00284B',
  compound: '#00D395',
  gnosis: '#00A6C4',
  lucidity: '#0D4BD1',
  maker: '#1AAB9B',
  masknetwork: '#1C68F3',
  monerium: '#322F31',
  muted: '#666',
  nori: '#26606a',
  oz: '#4E5EE4',
  paraswap: '#2669f5',
  pooltogether: '#8840FA'
  /* eslint-enable sort-keys-fix/sort-keys-fix */
}

/** @type {import('tailwindcss').Config} */
const theme = {
  content: [
    '../components/src/**/*.{tsx,ts}',
    '../ui/src/**/*.{tsx,ts}',
    '../../services/web/{components,pages}/**/*.{tsx,ts}'
  ],
  darkMode: ['selector', '[data-theme="dark"]'],
  plugins: [
    require('@tailwindcss/typography'),
    require('@tailwindcss/forms'),
    plugin(function ({ addVariant }) {
      addVariant('not-last', '&:not(:last-child)')
      addVariant('not-first', '&:not(:first-child)')
    })
  ],
  theme: {
    extend: {
      backgroundImage: () => ({
        'developer-onboarding-gradient': 'linear-gradient(180deg, #141618 0%, #8b99ff 181.41%)',
        'hero-pattern': "url('/images/bg_arrows.svg')",
        'mm-sdk-wizard-gradient':
          'linear-gradient(89deg, rgba(20, 22, 24, 0.00) 17.45%, #DE7A25 68.35%, #141618 111.08%)'
      }),
      borderWidth: {
        3: '3px'
      },
      boxShadow: {
        error: '0 0 0 0.125em #FF495C',
        hover: '0px 2px 4px rgba(black, .14)',
        inner: 'inset 0px 2px 8px rgba(18, 18, 18, 0.08)',
        'inner-heavy': 'inset 0px 2px 4px rgba(0, 0, 0, 0.42)',
        'inner-top': 'inset 0px 1px 20px rgba(0, 0, 0, 0.07)',
        lg: '0 4px 20px rgba(0,0,0,.14)',
        md: '0 4px 20px rgba(0,0,0,.07)',
        'outer-bottom': '0px 1px 4px rgba(0, 0, 0, 0.07)',
        'outer-right': '5px 0px 20px rgba(black, .14)',
        outline: `0 0 0 1px #D4D4D4`,
        'primary-outline-thick': `inset 0 0 0 2px var(--color-primary-default)`,
        tooltip: '0px 1px 20px 0px rgba(0, 0, 0, 0.07)'
      },
      colors: {
        /* eslint-disable sort-keys-fix/sort-keys-fix */
        ...thirdPartyColors,

        // Core Colours
        white: brandColor.white,
        black: brandColor.black,
        red: {
          25: brandColor.red025,
          50: brandColor.red050,
          100: brandColor.red100,
          200: brandColor.red200,
          300: brandColor.red300,
          400: brandColor.red400,
          500: brandColor.red500,
          600: brandColor.red600,
          700: brandColor.red700,
          800: brandColor.red800,
          900: brandColor.red900
        },
        orange: {
          25: brandColor.orange025,
          50: brandColor.orange050,
          100: brandColor.orange100,
          200: brandColor.orange200,
          300: brandColor.orange300,
          400: brandColor.orange400,
          500: brandColor.orange500,
          600: brandColor.orange600,
          700: brandColor.orange700,
          800: brandColor.orange800,
          900: brandColor.orange900
        },
        blue: {
          25: brandColor.blue025,
          50: brandColor.blue050,
          100: brandColor.blue100,
          200: brandColor.blue200,
          300: brandColor.blue300,
          400: brandColor.blue400,
          500: brandColor.blue500,
          600: brandColor.blue600,
          700: brandColor.blue700,
          800: brandColor.blue800,
          900: brandColor.blue900
        },
        green: {
          25: brandColor.green025,
          50: brandColor.green050,
          100: brandColor.green100,
          200: brandColor.green200,
          300: brandColor.green300,
          400: brandColor.green400,
          500: brandColor.green500,
          600: brandColor.green600,
          700: brandColor.green700,
          800: brandColor.green800,
          900: brandColor.green900
        },
        lime: {
          25: brandColor.lime025,
          50: brandColor.lime050,
          100: brandColor.lime100,
          200: brandColor.lime200,
          300: brandColor.lime300,
          400: brandColor.lime400,
          500: brandColor.lime500,
          600: brandColor.lime600,
          700: brandColor.lime700,
          800: brandColor.lime800,
          900: brandColor.lime900
        },
        purple: {
          25: brandColor.purple025,
          50: brandColor.purple050,
          100: brandColor.purple100,
          200: brandColor.purple200,
          300: brandColor.purple300,
          400: brandColor.purple400,
          500: brandColor.purple500,
          600: brandColor.purple600,
          700: brandColor.purple700,
          800: brandColor.purple800,
          900: brandColor.purple900
        },
        gray: {
          25: brandColor.grey025,
          50: brandColor.grey050,
          100: brandColor.grey100,
          200: brandColor.grey200,
          300: brandColor.grey300,
          400: brandColor.grey400,
          500: brandColor.grey500,
          600: brandColor.grey600,
          700: brandColor.grey700,
          800: brandColor.grey800,
          900: brandColor.grey900,
          1000: brandColor.grey1000
        },
        background: {
          alternative: {
            DEFAULT: 'var(--color-background-alternative)',
            hover: 'var(--color-background-alternative-)',
            pressed: 'var(--color-background-alternative-pressed)'
          },
          default: {
            DEFAULT: 'var(--color-background-default)',
            hover: 'var(--color-background-default-hover)',
            pressed: 'var(--color-background-default-pressed)'
          },
          hover: 'var(--color-background-hover)',
          pressed: 'var(--color-background-pressed)'
        },
        border: {
          default: 'var(--color-border-default)',
          muted: 'var(--color-border-muted)'
        },
        brand: brandColor,
        error: {
          alternative: 'var(--color-error-alternative)',
          default: {
            DEFAULT: 'var(--color-error-default)',
            hover: 'var(--color-error-default-hover)',
            pressed: 'var(--color-error-default-pressed)'
          },
          inverse: 'var(--color-error-inverse)',
          muted: 'var(--color-error-muted)'
        },
        flask: {
          default: 'var(--color-flask-default)',
          inverse: 'var(--color-flask-inverse)'
        },
        icon: {
          alternative: 'var(--color-icon-alternative)',
          default: 'var(--color-icon-default)',
          muted: 'var(--color-icon-muted)'
        },
        info: {
          default: 'var(--color-info-default)',
          inverse: 'var(--color-info-inverse)',
          muted: 'var(--color-info-muted)'
        },
        overlay: {
          alternative: 'var(--color-overlay-alternative)',
          default: 'var(--color-overlay-default)',
          inverse: 'var(--color-overlay-inverse)'
        },
        primary: {
          alternative: 'var(--color-primary-alternative)',
          default: {
            DEFAULT: 'var(--color-primary-default)',
            hover: 'var(--color-primary-default-hover)',
            pressed: 'var(--color-primary-default-pressed)'
          },
          inverse: 'var(--color-primary-inverse)',
          muted: 'var(--color-primary-muted)'
        },
        shadow: {
          default: 'var(--color-shadow-default)',
          error: 'var(--color-shadow-error)',
          primary: 'var(--color-shadow-primary)'
        },
        success: {
          default: {
            DEFAULT: 'var(--color-success-default)',
            hover: 'var(--color-success-default-hover)',
            pressed: 'var(--color-success-default-pressed)'
          },
          inverse: 'var(--color-success-inverse)',
          muted: 'var(--color-success-muted)'
        },
        text: {
          alternative: 'var(--color-text-alternative)',
          default: 'var(--color-text-default)',
          muted: 'var(--color-text-muted)'
        },
        warning: {
          default: {
            DEFAULT: 'var(--color-warning-default)',
            hover: 'var(--color-warning-default-hover)',
            pressed: 'var(--color-warning-default-pressed)'
          },
          inverse: 'var(--color-warning-inverse)',
          muted: 'var(--color-warning-muted)'
        },

        code: {
          background: '#002B36'
        },

        dark: '#6666664D',

        disabled: '#999',

        secondary: '#327F94',

        // Stats colors (Pre Stats v1.5 - DEPRECATED)

        teal: {
          100: '#E1F0F5',
          200: '#B7DCE7',
          300: '#89C6D7',
          400: '#4CA9C2',
          500: '#327F94',
          600: '#2D7285',
          700: '#327F94',
          800: '#20525F',
          900: '#183E48'
        },
        transparent: 'transparent',
        yellow: {
          100: '#FFF8E0',
          200: '#FFF0B8',
          300: '#FFE894',
          400: '#FFDD61',
          500: '#FFD333',
          600: '#F0BC00',
          700: '#D1A400',
          800: '#AD8800',
          900: '#806400'
        }
        /* eslint-enable sort-keys-fix/sort-keys-fix */
      },
      dropShadow: {
        '2xl': '0 25px 25px rgb(0 0 0 / 0.15)',
        DEFAULT: '0px 1px 4px rgba(black, .07)',
        lg: '0px 4px 20px rgba(black, .14)',
        md: '0px 4px 20px rgba(black, .07)',
        none: '0 0 #0000',
        sm: '0px 1px 2px rgba(black, .05)',
        xl: ['0 20px 13px rgb(0 0 0 / 0.03)', '0 8px 5px rgb(0 0 0 / 0.08)']
      },
      gridTemplateColumns: {
        '2-1': '2fr 1fr'
      },
      inset: {
        '1/2': '50%'
      },
      minHeight: theme => ({
        ...theme('maxHeight')
      }),
      minWidth: theme => ({
        ...theme('maxWidth'),
        '1/2': '50%',
        '1/3': '33.333333%',
        '1/4': '25%',
        '2/3': '66.666667%',
        '3/4': '75%'
      }),
      opacity: {
        disabled: '0.5'
      },
      scale: {
        60: '0.6',
        65: '0.65',
        70: '0.70',
        75: '0.75'
      },
      screens: {
        /* eslint-disable sort-keys-fix/sort-keys-fix */
        xs: '420px',
        sm: '640px',
        phone: '640px',
        md: '768px',
        tablet: '769px',
        desktop: '960px',
        lg: '1024px',
        wide: '1152px',
        hd: '1344px',
        xl: '1392px',
        xxl: '1441px',
        '3xl': '1500px'
        /* eslint-enable sort-keys-fix/sort-keys-fix */
      },
      spacing: {
        '2xl': '24rem',
        14: '3.5rem',
        16: '4rem',
        18: '4.5rem',
        30: '7.5rem',
        50: '12.5rem',
        65: '16.25rem',
        70: '17.5rem',
        96: '24rem',
        105: '26.5rem',
        140: '35rem',
        160: '40rem',
        180: '47.5rem',
        base: '1.5rem',
        lg: '4rem',
        med: '3rem',
        reg: '2rem',
        sm: '1rem',
        xl: '7.5rem',
        xs: '0.5rem'
      },
      transitionDuration: {
        200: '200ms'
      },
      transitionTimingFunction: {
        in: 'cubic-bezier(0.4, 0, 1, 1)',
        'in-out': 'cubic-bezier(0.4, 0, 0.2, 1)'
      }
    },
    fontFamily: {
      body: [`Euclid Circular B`, ...defaultTheme.fontFamily.sans],
      bold: [`Euclid Circular B`, ...defaultTheme.fontFamily.sans],
      light: [`Euclid Circular B`, ...defaultTheme.fontFamily.sans],
      manrope: [`Manrope Bold`, ...defaultTheme.fontFamily.sans],
      mono: [`Akkurat Mono`, 'Consolas', '"Liberation Mono"', '"Courier New"', 'monospace'],
      sans: [`Euclid Circular B`, ...defaultTheme.fontFamily.sans]
    },
    fontSize: {
      '2xl': '1.5rem',
      '3xl': '1.75rem',
      '4xl': '2.25rem',
      '5xl': '2.875rem',
      '6xl': '4rem',
      '7xl': '5rem',
      '8xl': '6rem',
      '9xl': '7rem',
      '10xl': '8rem',
      base: '1rem',
      lg: '1.125rem',
      sm: '.875rem',
      xl: '1.25rem',
      xs: '.75rem',
      xxs: '.625rem'
    }
  }
}
module.exports = theme
